import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

const METABASE_SCRIPT_ID = 'metabase-embed-js';

const ensureMetabaseEmbedScript = (instanceUrl) =>
  new Promise((resolve, reject) => {
    if (!instanceUrl) {
      reject(new Error('Metabase InstanceUrl nao informado.'));
      return;
    }

    const existing = document.getElementById(METABASE_SCRIPT_ID);
    if (existing) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.id = METABASE_SCRIPT_ID;
    script.defer = true;
    script.src = `${String(instanceUrl).replace(/\/$/, '')}/app/embed.js`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Falha ao carregar embed.js do Metabase.'));
    document.body.appendChild(script);
  });

const MetabaseDashboardEmbed = ({
  instanceUrl,
  token,
  themePreset,
  isGuest,
  withTitle,
  withDownloads,
  initialParameters,
  style,
  locale
}) => {
  // IMPORTANT:
  // The Metabase embed script upgrades <metabase-dashboard> and runs connectedCallback
  // immediately after script execution. If window.metabaseConfig is not defined yet,
  // Metabase will throw: "instanceUrl must be provided".
  // So we MUST define window.metabaseConfig synchronously before the script is loaded.
  if (typeof window !== 'undefined' && instanceUrl) {
    window.defineMetabaseConfig = (config) => {
      window.metabaseConfig = config;
    };

    window.metabaseConfig = {
      theme: { preset: themePreset || 'light' },
      isGuest: typeof isGuest === 'boolean' ? isGuest : true,
      instanceUrl,
      locale: locale || 'pt-BR'
    };
  }

  useEffect(() => {
    if (!instanceUrl) return;
    ensureMetabaseEmbedScript(instanceUrl).catch(() => {
      // intencional: a tela consumidora exibe erro/placeholder
    });
  }, [instanceUrl]);

  const parametersAttr = useMemo(() => {
    if (!initialParameters) return undefined;
    if (typeof initialParameters === 'string') return initialParameters;
    try {
      return JSON.stringify(initialParameters);
    } catch {
      return undefined;
    }
  }, [initialParameters]);

  if (!instanceUrl || !token) return null;

  return (
    <div style={{ minHeight: 600, ...style }}>
      {/* eslint-disable-next-line react/no-unknown-property */}
      <metabase-dashboard
        token={token}
        with-title={withTitle ? 'true' : 'false'}
        with-downloads={withDownloads ? 'true' : 'false'}
        locale={locale || 'pt-BR'}
        {...(parametersAttr ? { 'initial-parameters': parametersAttr } : {})}
        style={{
          width: '100%',
          height: 'calc(100vh - 260px)',
          display: 'block'
        }}
      />
    </div>
  );
};

MetabaseDashboardEmbed.propTypes = {
  instanceUrl: PropTypes.string,
  token: PropTypes.string,
  themePreset: PropTypes.oneOf(['light', 'dark']),
  isGuest: PropTypes.bool,
  withTitle: PropTypes.bool,
  withDownloads: PropTypes.bool,
  initialParameters: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  style: PropTypes.object,
  locale: PropTypes.string
};

MetabaseDashboardEmbed.defaultProps = {
  instanceUrl: '',
  token: '',
  themePreset: 'light',
  isGuest: true,
  withTitle: false,
  withDownloads: false,
  initialParameters: undefined,
  style: undefined,
  locale: 'pt-BR'
};

export default MetabaseDashboardEmbed;
